import React, { FC } from 'react';
import styled from 'styled-components';

import { BusinessHoursCondition as BusinessHoursConditionType } from 'src/actions/rules/condition';
import DeleteButton from 'src/presentation/components/pages/shared/DeleteButton';
import { DropdownMenu } from 'src/presentation/components/agent_desktop_components';
import { InternalLink } from 'src/presentation/components/pages/shared/Links';
import {
  StyledTypeDescription,
  StyledRowItem,
  StyledEntityRow,
  StyledDropdown,
} from 'src/presentation/components/pages/rules/shared/StyledComponents';
import useBusinessHours from 'src/presentation/components/pages/rules/hooks/useBusinessHours';

type BusinessHoursConditionProps = {
  condition: BusinessHoursConditionType;
  index: number;
  onDelete: Function;
  onUpdate: Function;
};

enum Inclusions {
  INSIDE = 'INSIDE',
  OUTSIDE = 'OUTSIDE',
}

const inclusionOptions = [
  { value: Inclusions.INSIDE, label: 'Is inside business hours' },
  { value: Inclusions.OUTSIDE, label: 'Is outside business hours' },
];

const BusinessHoursCondition: FC<BusinessHoursConditionProps> = ({ condition, index, onDelete, onUpdate }) => {
  const onClickDelete = () => {
    onDelete(index);
  };

  const onValueChange = val => {
    condition.setValue(val);
    onUpdate({ condition, index });
  };

  const onInclusionChange = val => {
    condition.setInclusion(val === Inclusions.INSIDE);
    onUpdate({ condition, index });
  };

  const { loadingState, values: businessHoursOptions } = useBusinessHours();

  if (loadingState === 'loading') return null;

  const renderBusinessHoursLink = condition => (
    <StyledInternalLink data-aid="view-business-hours" target="_blank" to={`/business-hours/${condition.value}`}>
      View Business Hours
    </StyledInternalLink>
  );

  return (
    <>
      <StyledEntityRow>
        <StyledRowItem>And</StyledRowItem>
        <StyledTypeDescription>Business Hours are</StyledTypeDescription>
        <BusinessHoursList
          data-aid="business-hours-selector"
          searchable
          onSelect={onValueChange}
          options={businessHoursOptions}
          placeholder="Select business hours"
          value={condition.value}
        />
        {condition.value && renderBusinessHoursLink(condition)}
        <StyledDeleteButton id={index} onClick={onClickDelete} />
      </StyledEntityRow>
      {condition.value && (
        <SpacedBusinessHoursEntityRow>
          <StyledRowItem>And</StyledRowItem>
          <StyledDropdown
            onSelect={onInclusionChange}
            options={inclusionOptions}
            value={condition.inclusion ? Inclusions.INSIDE : Inclusions.OUTSIDE}
          />
        </SpacedBusinessHoursEntityRow>
      )}
    </>
  );
};

const StyledDeleteButton = styled(DeleteButton)`
  margin-left: 8px;
`;

const BusinessHoursList = styled(DropdownMenu)`
  width: 230px;
`;

const SpacedBusinessHoursEntityRow = styled(StyledEntityRow)`
  margin-top: 8px;
`;

const StyledInternalLink = styled(InternalLink)`
  color: ${p => p.theme.colors.green400};
  margin-left: auto;
`;

export default BusinessHoursCondition;
